import { useState } from "react";
import Modal from "../components/Modal";
import GuideBanner from "../components/GuideBanner";
import Field, { inputStyle, selectStyle, inputErrorStyle } from "../components/Field";
import { REGIONS, FLEET_SIZES, CONTACT_CHANNELS, CLIENT_SYSTEMS } from "../constants";
import { generateId, getToday, validateEmail, validatePhone, maskPhone, maskCNPJ, maskCurrency, unmaskCurrency, fetchCNPJ } from "../utils";

const empty = {
  company: "", contact: "", title: "", phone: "", email: "", whatsapp: "",
  cnpj: "", region: "Grande São Paulo", fleetSize: "", vehicleTypes: [], services: [],
  source: "WhatsApp", notes: "", estimatedValue: "", contractMonths: 12,
  systems: [], razaoSocial: "", nomeFantasia: "", address: "",
};

export default function NewClient({ open, onClose, setClients, setTasks, userId }) {
  const [data, setData] = useState({ ...empty });
  const [errors, setErrors] = useState({});
  const [cnpjLoading, setCnpjLoading] = useState(false);

  const validate = () => {
    const err = {};
    if (!data.company.trim()) err.company = "Nome da empresa é obrigatório";
    if (data.phone && !validatePhone(data.phone)) err.phone = "Telefone inválido";
    if (data.email && !validateEmail(data.email)) err.email = "E-mail inválido";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleCNPJ = async (val) => {
    const masked = maskCNPJ(val);
    setData((p) => ({ ...p, cnpj: masked }));
    const digits = masked.replace(/\D/g, "");
    if (digits.length === 14) {
      setCnpjLoading(true);
      const result = await fetchCNPJ(digits);
      setCnpjLoading(false);
      if (result) {
        setData((p) => ({
          ...p,
          company: p.company || result.nomeFantasia || result.razaoSocial,
          razaoSocial: result.razaoSocial,
          nomeFantasia: result.nomeFantasia,
          address: result.enderecoCompleto || p.address,
        }));
      }
    }
  };

  const toggleSystem = (sysId) => {
    setData((p) => ({
      ...p,
      systems: p.systems.includes(sysId)
        ? p.systems.filter((s) => s !== sysId)
        : [...p.systems, sysId],
    }));
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const today = getToday();
    const c = {
      ...data, id: generateId(), stage: "novo_lead", starred: false, userId,
      createdAt: today, lastContact: null, interactions: [],
      stageHistory: [{ from: null, to: "novo_lead", date: today, note: "Lead cadastrado" }],
      lossReason: null, nextAction: "Fazer primeiro contato", nextActionDate: today,
      estimatedValue: unmaskCurrency(data.estimatedValue) || 0,
    };
    setClients((p) => [...p, c]);
    setTasks((p) => [...p, {
      id: generateId(), title: `Fazer 1º contato — ${c.company}`, userId,
      type: "client", refId: c.id, dueDate: today, priority: "high", done: false,
      notes: "Novo lead cadastrado. Fazer primeiro contato.",
    }]);
    setData({ ...empty });
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setData({ ...empty });
    setErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Cadastrar Novo Lead — Cliente com Frota">
      <GuideBanner title="Dica: Como cadastrar um novo lead" text="Preencha o nome da empresa e CNPJ (puxará dados automáticos). Contato e telefone podem ser informados depois ao registrar a primeira abordagem." />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Field label="CNPJ">
          <div style={{ position: "relative" }}>
            <input style={inputStyle} value={data.cnpj} onChange={(e) => handleCNPJ(e.target.value)} placeholder="00.000.000/0000-00" />
            {cnpjLoading && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#64748b" }}>Buscando...</span>}
          </div>
        </Field>
        <Field label="Como chegou até nós?">
          <select style={selectStyle} value={data.source} onChange={(e) => setData((p) => ({ ...p, source: e.target.value }))}>
            {CONTACT_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Nome da Empresa" help="Razão social ou nome fantasia." required error={errors.company}>
            <input style={errors.company ? inputErrorStyle : inputStyle} value={data.company} onChange={(e) => setData((p) => ({ ...p, company: e.target.value }))} placeholder="Ex: Logística Express Ltda" />
          </Field>
        </div>
        {data.razaoSocial && (
          <div style={{ gridColumn: "1/-1", padding: "6px 10px", borderRadius: 6, background: "#f0fdfa", border: "1px solid #99f6e4", marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: "#0f766e" }}><strong>Razão Social:</strong> {data.razaoSocial}</div>
            {data.nomeFantasia && <div style={{ fontSize: 10, color: "#0f766e" }}><strong>Nome Fantasia:</strong> {data.nomeFantasia}</div>}
            {data.address && <div style={{ fontSize: 10, color: "#0f766e" }}><strong>Endereço:</strong> {data.address}</div>}
          </div>
        )}

        {/* Sistema potencial - cards selecionáveis */}
        <div style={{ gridColumn: "1/-1", marginBottom: 8 }}>
          <Field label="Sistema que o cliente utiliza/precisa" help="Selecione um ou mais sistemas">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {CLIENT_SYSTEMS.map((sys) => {
                const selected = data.systems.includes(sys.id);
                return (
                  <div key={sys.id} onClick={() => toggleSystem(sys.id)} style={{
                    flex: "1 1 100px", padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                    background: selected ? sys.color + "15" : "#f8fafc",
                    border: `2px solid ${selected ? sys.color : "#e2e8f0"}`,
                    textAlign: "center", transition: "all 0.15s",
                  }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: sys.color, margin: "0 auto 4px" }} />
                    <div style={{ fontSize: 11, fontWeight: 600, color: selected ? sys.color : "#64748b", marginTop: 2 }}>{sys.label}</div>
                    {selected && <div style={{ fontSize: 9, color: sys.color, marginTop: 2 }}>✓ Selecionado</div>}
                  </div>
                );
              })}
            </div>
          </Field>
        </div>

        <Field label="Região">
          <select style={selectStyle} value={data.region} onChange={(e) => setData((p) => ({ ...p, region: e.target.value }))}>
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Tamanho da Frota" error={errors.fleetSize}>
          <select style={errors.fleetSize ? inputErrorStyle : selectStyle} value={data.fleetSize} onChange={(e) => setData((p) => ({ ...p, fleetSize: e.target.value }))}>
            <option value="">Selecione...</option>
            {FLEET_SIZES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </Field>
        <Field label="Valor Estimado (R$)" help="Estimativa anual do contrato.">
          <input style={inputStyle} value={data.estimatedValue} onChange={(e) => setData((p) => ({ ...p, estimatedValue: maskCurrency(e.target.value) }))} placeholder="0,00" />
        </Field>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Observações">
            <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={data.notes} onChange={(e) => setData((p) => ({ ...p, notes: e.target.value }))} placeholder="Ex: Empresa usa planilha para controlar manutenção." />
          </Field>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 6 }}>
        <button onClick={handleClose} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
        <button onClick={handleSubmit} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#0d9488", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cadastrar Lead</button>
      </div>
    </Modal>
  );
}
